import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'dashboardApi',
  initialState,
  reducers: {
    someAction(state, action: PayloadAction<any>) {},
  },
});

export const api = createApi({
  reducerPath: 'dashboardApi',
  baseQuery,
  endpoints: builder => ({
    getStats: builder.query<any, any>({
      query: () => ({
        url: endpoints.DASHBOARD_STATS,
        method: 'GET',
      }),
    }),
    getRecentUsers: builder.query<any, void>({
      query: () => ({
        url: endpoints.RECENT_USERS,
        method: 'GET',
      }),
    }),
    getRecentOrders: builder.query<any, void>({
      query: () => ({
        url: endpoints.RECENT_ORDERS,
        method: 'GET',
      }),
    }),
    getRecentRequestFiles: builder.query<any, void>({
      query: () => ({
        url: endpoints.RECENT_REQUEST_FILES,
        method: 'GET',
      }),
    }),
    getTopFileDownloads: builder.query<any, void>({
      query: () => ({
        url: endpoints.TOP_FILE_DOWNLOADS,
        method: 'GET',
      }),
    }),
    getRecentDownloads: builder.query<any, void>({
      query: () => ({
        url: endpoints.RECENT_DOWNLOADS,
        method: 'GET',
      }),
    }),
    getRecentWalletTransactions: builder.query<any, void>({
      query: () => ({
        url: endpoints.RECENT_WALLET_TRANSACTIONS,
        method: 'GET',
      }),
    }),
    getRecentEnquiry: builder.query<any, void>({
      query: () => ({
        url: endpoints.RECENT_ENQUIRY,
        method: 'GET',
      }),
    }),
    getRecentTransactions: builder.query<any, void>({
      query: () => ({
        url: endpoints.RECENT_TRANSACTIONS,
        method: 'GET',
      }),
    }),
    chartUsers: builder.query<any, any>({
      query: filters => ({
        url: endpoints.CHART_USERS,
        method: 'GET',
        params: filters,
      }),
    }),
    chartOrders: builder.query<any, any>({
      query: filters => ({
        url: endpoints.CHART_ORDERS,
        method: 'GET',
        params: filters,
      }),
    }),
    chartsDownloads: builder.query<any, any>({
      query: filters => ({
        url: endpoints.CHART_DOWNLOADS,
        method: 'GET',
        params: filters,
      }),
    }),
    chartTransactions: builder.query<any, any>({
      query: filters => ({
        url: endpoints.CHART_TRANSACTIONS,
        method: 'GET',
        params: filters,
      }),
    }),
    gaOverview: builder.query<any, { startDate: string; endDate: string }>({
      query: params => ({
        url: endpoints.GA_OVERVIEW,
        method: 'GET',
        params,
      }),
    }),
    gaPageViews: builder.query<any, { startDate: string; endDate: string }>({
      query: params => ({
        url: endpoints.GA_PAGE_VIEWS,
        method: 'GET',
        params,
      }),
    }),
    gaAcquisition: builder.query<any, { startDate: string; endDate: string }>({
      query: params => ({
        url: endpoints.GA_ACQUISITION,
        method: 'GET',
        params,
      }),
    }),
    gaDemographics: builder.query<any, { startDate: string; endDate: string }>({
      query: params => ({
        url: endpoints.GA_DEMOGRAPHICS,
        method: 'GET',
        params,
      }),
    }),
    gaTopPages: builder.query<any, { startDate: string; endDate: string }>({
      query: params => ({
        url: endpoints.GA_TOP_PAGES,
        method: 'GET',
        params,
      }),
    }),
    gaActiveUsers: builder.query<any, void>({
      query: () => ({
        url: endpoints.GA_ACTIVE_USERS,
        method: 'GET',
      }),
      pollInterval: 30000,
    }),
    gaActiveUsersHistory: builder.query<
      any,
      { startDate: string; endDate: string }
    >({
      query: params => ({
        url: endpoints.GA_ACTIVE_USERS_HISTORY,
        method: 'GET',
        params,
      }),
    }),
    gaConfig: builder.query<any, void>({
      query: () => ({
        url: endpoints.GA_CONFIG,
        method: 'GET',
      }),
    }),
    updateGaConfig: builder.mutation<any, { propertyId: string; credentials?: string }>({
      query: body => ({
        url: endpoints.GA_CONFIG,
        method: 'POST',
        body,
      }),
    }),
    testGaConfig: builder.mutation<any, { propertyId?: string; credentials?: string }>({
      query: body => ({
        url: endpoints.GA_CONFIG_TEST,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const { actions: UsersActions } = slice;

export const useDashboardSlice = () => {
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  //  useInjectSaga({ key: slice.name, saga: Saga });
  return {
    actions: UsersActions,
    useGetStatsQuery: api.useLazyGetStatsQuery,
    useGetRecentUsersQuery: api.useLazyGetRecentUsersQuery,
    useGetRecentOrdersQuery: api.useLazyGetRecentOrdersQuery,
    useGetRecentRequestFile: api.useLazyGetRecentRequestFilesQuery,
    useGetTopFileDownloadsQuery: api.useLazyGetTopFileDownloadsQuery,
    useGetRecentEnquiryQuery: api.useLazyGetRecentEnquiryQuery,
    useGetUsersChartQuery: api.useLazyChartUsersQuery,
    useGetOrdersChartQuery: api.useLazyChartOrdersQuery,
    useGetRecentTransactionsQuery: api.useLazyGetRecentTransactionsQuery,
    useGetRecentDownloadsQuery: api.useLazyGetRecentDownloadsQuery,
    useGetRecentWalletTransactionsQuery:
      api.useLazyGetRecentWalletTransactionsQuery,
    useGetDownloadsChartQuery: api.useLazyChartsDownloadsQuery,
    useGetTransactionsChartQuery: api.useLazyChartTransactionsQuery,
    useGetGaOverviewQuery: api.useLazyGaOverviewQuery,
    useGetGaPageViewsQuery: api.useLazyGaPageViewsQuery,
    useGetGaAcquisitionQuery: api.useLazyGaAcquisitionQuery,
    useGetGaDemographicsQuery: api.useLazyGaDemographicsQuery,
    useGetGaTopPagesQuery: api.useLazyGaTopPagesQuery,
    useGetGaActiveUsersQuery: api.useLazyGaActiveUsersQuery,
    useGetGaActiveUsersHistoryQuery: api.useLazyGaActiveUsersHistoryQuery,
    useGaConfigQuery: api.useGaConfigQuery,
    useUpdateGaConfigMutation: api.useUpdateGaConfigMutation,
    useTestGaConfigMutation: api.useTestGaConfigMutation,
  };
};
